/**
 * SEO landing-page templates — driven by the LANDING_PAGES data map.
 *
 * Each entry produces a fully-formed marketing page targeting a specific
 * keyword cluster. Structure:
 *   • Semantic H1 / H2 / H3 hierarchy with the target keyword in the H1
 *   • Page hero with strong CTAs (Get Started Free + Request School Pilot)
 *   • "Why ActanonEdu" + "How it works" + "Features" sections
 *   • FAQ accordion (also emits FAQ schema)
 *   • Breadcrumb schema (Home → cluster → page)
 *   • CTA strip + internal links to related landing pages
 *
 * Adding a new SEO landing page = adding a key to LANDING_PAGES — no JSX edit.
 */
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Check, Sparkles, Languages, ShieldCheck } from 'lucide-react';
import MarketingLayout from './shared/MarketingLayout';
import { PageHero, Section, SectionHeader, FAQAccordion, CTAStrip, FeatureCard } from './shared/blocks';
import { TrustBadges } from './shared/PremiumBlocks';
import { Button } from '../../components/ui/button';
import SEO from './shared/SEO';
import { AI_TOOLS, ERP_MODULES, BRAND } from './content';

// ─── Landing page data map ──────────────────────────────
// Used by both the data-driven router below AND the sitemap.
export const LANDING_PAGES = {
  // ─── Cluster 1 · Teachers ───
  'ai-tool-for-teachers': {
    h1: 'AI Tool for Teachers — Built for the Indian Classroom',
    accent: '',
    eyebrow: 'AI for educators',
    description: 'ActanonEdu is the AI tool for teachers that generates lesson plans, worksheets, quizzes and question papers — board-aligned, multilingual, and ready to download as DOCX or PDF in minutes.',
    keywords: ['AI tool for teachers', 'AI teaching assistant', 'teacher productivity', 'AI for educators', 'lesson plan AI'],
    benefits: [
      { icon: Sparkles,    title: 'Saves 15+ hours per week', desc: 'From Sunday-night question papers to minute-by-minute lesson plans, in one click.' },
      { icon: Languages,   title: '7 Indian languages',       desc: 'Native generation — not translation. Grade-band aware tone.' },
      { icon: ShieldCheck, title: 'Data hosted in India',     desc: 'Servers in India. AES-256 encryption. Signed DPA for schools.' },
    ],
    related: ['ai-lesson-plan-generator', 'worksheet-generator', 'quiz-generator', 'teacher-productivity-platform'],
  },
  'teacher-productivity-platform': {
    h1: 'Teacher Productivity Platform for Indian Schools',
    accent: '',
    eyebrow: 'Teacher productivity',
    description: 'The complete teacher productivity platform: AI content engine + school ERP + parent communication, in one login. Designed for the workload of an Indian teacher.',
    keywords: ['teacher productivity platform', 'teacher software India', 'teacher dashboard', 'AI for teachers'],
    benefits: [
      { icon: Sparkles,    title: 'One login, one workflow',  desc: 'Lesson prep, attendance, marks, parent chat — never tab-switch again.' },
      { icon: Languages,   title: 'Built for Indian boards',  desc: 'CBSE · ICSE · IGCSE · IB · CIS · State Boards · grade-1 to grade-12.' },
      { icon: ShieldCheck, title: 'Safe for student data',    desc: 'Privacy-first. India-hosted. Never trains AI on your data.' },
    ],
    related: ['ai-tool-for-teachers', 'ai-lesson-plan-generator', 'school-erp-software'],
  },

  // ─── Cluster 2 · Content creation ───
  'ai-lesson-plan-generator': {
    h1: 'AI Lesson Plan Generator — Board-Aligned, Multilingual',
    accent: '',
    eyebrow: 'Lesson plans · in minutes',
    description: 'Generate detailed, board-aligned lesson plans in seconds. CBSE, ICSE, IGCSE, IB, CIS and State Boards supported. Multilingual output in English, Hindi, Marathi, Urdu, Punjabi, Bengali and Tamil.',
    keywords: ['AI lesson plan generator', 'lesson plan AI', 'CBSE lesson plan', 'ICSE lesson plan', 'multilingual lesson plan'],
    benefits: [
      { icon: Sparkles,    title: 'Activity-by-minute',      desc: 'Time-budgeted activities that always sum to your class period.' },
      { icon: Check,       title: 'Multi-day units',         desc: 'Day-wise planner for chapters spanning a week.' },
      { icon: Languages,   title: 'Localise in one click',   desc: 'Switch the entire plan to Hindi, Marathi or Tamil instantly.' },
    ],
    related: ['hindi-lesson-plan-generator', 'worksheet-generator', 'ai-tool-for-teachers'],
  },
  'worksheet-generator': {
    h1: 'Worksheet Generator for Teachers — DOCX & PDF Ready',
    accent: '',
    eyebrow: 'Worksheets · auto-graded',
    description: 'AI worksheet generator with answer keys, difficulty controls, and DOCX/PDF download. Renders scientific notation correctly. Supports CBSE, ICSE, IGCSE and State Boards.',
    keywords: ['worksheet generator', 'worksheet maker', 'CBSE worksheet generator', 'AI worksheet', 'printable worksheet'],
    benefits: [
      { icon: Sparkles,    title: 'Difficulty mix you control', desc: 'Easy / Medium / Hard ratios set by slider — never one-note again.' },
      { icon: Check,       title: 'Answer keys + marking',     desc: 'Auto-generated marking scheme alongside every worksheet.' },
      { icon: Languages,   title: 'Scientific notation safe',  desc: 'H₂O, x², ½ rendered correctly in DOCX and PDF.' },
    ],
    related: ['ai-lesson-plan-generator', 'quiz-generator', 'hindi-worksheet-generator', 'marathi-worksheet-generator'],
  },
  'quiz-generator': {
    h1: 'AI Quiz Generator — MCQ, True/False, Fill, Short Answer',
    accent: '',
    eyebrow: 'Quizzes · in seconds',
    description: 'AI quiz generator for teachers — Bloom-aligned MCQs with intelligent distractors, true/false, fill-in-the-blanks, short answer. Board-pattern aware.',
    keywords: ['AI quiz generator', 'quiz maker', 'MCQ generator', 'online quiz maker for teachers'],
    benefits: [
      { icon: Sparkles, title: 'Bloom-aligned',          desc: 'Choose cognitive level: Remember → Apply → Analyse.' },
      { icon: Check,    title: 'Smart distractors',      desc: 'MCQ wrong answers test real misconceptions, not random text.' },
      { icon: Check,    title: 'Multiple exam patterns', desc: 'CBSE Section A–E · IGCSE Paper 1/2 · IB IA.' },
    ],
    related: ['question-paper-generator', 'worksheet-generator', 'bengali-quiz-generator'],
  },
  'question-paper-generator': {
    h1: 'Question Paper Generator — Board-Pattern Aware',
    accent: '',
    eyebrow: 'Question papers',
    description: 'AI question paper generator that matches your school blueprint. Section-wise marks distribution validated by math reconciler. CBSE · ICSE · IGCSE · IB.',
    keywords: ['question paper generator', 'AI question paper', 'CBSE question paper maker', 'blueprint matching'],
    benefits: [
      { icon: Sparkles,    title: 'Blueprint upload',     desc: 'Upload your school template — output matches exactly.' },
      { icon: Check,       title: 'Validated arithmetic', desc: 'Section marks always equal sum of items. No silent errors.' },
      { icon: ShieldCheck, title: 'Internal choice handling', desc: 'Per-board internal-choice rules applied automatically.' },
    ],
    related: ['tamil-question-paper-generator', 'worksheet-generator', 'ai-lesson-plan-generator'],
  },

  // ─── Cluster 3 · School ERP ───
  'school-erp-software': {
    h1: 'School ERP Software — Designed for Indian Schools',
    accent: '',
    eyebrow: 'School ERP',
    description: 'Complete School ERP Software: attendance, fees, communication, student records, academics, administration. Built for Indian schools — by Indian operations heads.',
    keywords: ['school ERP software', 'school management software', 'school administration software India'],
    benefits: [
      { icon: Sparkles,    title: 'Six modules, one login',     desc: 'Attendance · Fees · Communication · Records · Academics · Admin.' },
      { icon: Check,       title: 'Razorpay-integrated fees',   desc: 'Installments, late-fee rules, auto receipts in PDF.' },
      { icon: ShieldCheck, title: 'Data dignity',              desc: 'India-hosted. School-grade access controls. Audit trail.' },
    ],
    related: ['school-management-system', 'student-information-system', 'attendance-management-system'],
  },
  'school-management-system': {
    h1: 'School Management System for India — End-to-End',
    accent: '',
    eyebrow: 'Operations',
    description: 'A school management system that handles the full lifecycle — admissions, attendance, fees, timetable, marks, parent comms. From enquiry to alumni.',
    keywords: ['school management system', 'school operations software', 'SMS India schools'],
    benefits: [
      { icon: Sparkles, title: 'Admissions → alumni',          desc: 'Single source of truth for the full student lifecycle.' },
      { icon: Check,    title: 'Timetable & substitutes',      desc: 'Drag-drop builder · last-minute substitute assistant.' },
      { icon: Check,    title: 'Parent communication built-in', desc: 'WhatsApp + email broadcasts with read receipts.' },
    ],
    related: ['school-erp-software', 'student-information-system', 'attendance-management-system'],
  },
  'student-information-system': {
    h1: 'Student Information System — Beyond a Spreadsheet',
    accent: '',
    eyebrow: 'SIS',
    description: 'Modern Student Information System for Indian schools. Admissions pipeline, transfer certificates, photo IDs, academic history — all in one place.',
    keywords: ['student information system', 'SIS India', 'student records software', 'admissions software'],
    benefits: [
      { icon: Sparkles, title: 'Admissions pipeline',     desc: 'From enquiry → application → confirmed admission.' },
      { icon: Check,    title: 'TC & ID generation',      desc: 'One-click Transfer Certificate and photo ID.' },
      { icon: Check,    title: 'Lifecycle visibility',    desc: 'See a student\'s journey from enrolment to alumni status.' },
    ],
    related: ['school-erp-software', 'school-management-system', 'attendance-management-system'],
  },
  'attendance-management-system': {
    h1: 'Attendance Management System — Daily & Period-wise',
    accent: '',
    eyebrow: 'Attendance',
    description: 'A modern attendance management system: daily roll-call, period-wise attendance, automatic parent SMS on absence, monthly reports for class teachers.',
    keywords: ['attendance management system', 'school attendance app', 'attendance software India'],
    benefits: [
      { icon: Sparkles, title: '10 seconds per class',  desc: 'Click-once interface — no scrolling through hundreds of names.' },
      { icon: Check,    title: 'Period-wise tracking',  desc: 'Capture per-subject attendance for senior classes.' },
      { icon: Check,    title: 'Parent SMS on absence', desc: 'Automatic same-day parent notification — no manual followup.' },
    ],
    related: ['school-erp-software', 'school-management-system', 'student-information-system'],
  },

  // ─── Cluster 4 · Multilingual India ───
  'multilingual-ai-tool-for-teachers': {
    h1: 'Multilingual AI Tool for Teachers — 7 Indian Languages',
    accent: '',
    eyebrow: 'Multilingual India',
    description: 'The first AI platform for Indian teachers with native multilingual generation in English, Hindi, Marathi, Urdu, Punjabi, Bengali and Tamil — with Telugu, Kannada, Malayalam, Gujarati, Odia, Assamese and Sanskrit launching through 2026.',
    keywords: ['multilingual AI tool for teachers', 'AI platform for Indian teachers', 'regional language AI'],
    benefits: [
      { icon: Languages, title: '7 active languages',  desc: 'Native generation — not translation. Subject-aware register.' },
      { icon: Sparkles,  title: '7 more in 2026',      desc: 'Telugu, Kannada, Malayalam, Gujarati, Odia, Assamese, Sanskrit.' },
      { icon: Check,     title: 'Grade-band aware',    desc: 'A Class 4 worksheet sounds like Class 4. Always.' },
    ],
    related: ['hindi-lesson-plan-generator', 'marathi-worksheet-generator', 'tamil-question-paper-generator', 'urdu-teaching-material-generator'],
  },
  'hindi-lesson-plan-generator': {
    h1: 'Hindi Lesson Plan Generator — हिंदी में पाठ-योजना',
    accent: '',
    eyebrow: 'हिंदी · Hindi',
    description: 'Generate Hindi lesson plans in seconds — board-aligned, grade-band aware, with native Devanagari script. Best Hindi lesson plan generator for CBSE & State Board teachers.',
    keywords: ['Hindi lesson plan generator', 'हिंदी पाठ योजना', 'CBSE Hindi lesson plan'],
    benefits: [
      { icon: Languages, title: 'Native Devanagari',       desc: 'Not transliteration — proper Hindi with correct register.' },
      { icon: Check,     title: 'Grade-band tone',         desc: 'Sounds like a Hindi-medium textbook for that class — not college essay.' },
      { icon: Check,     title: 'Subject-aware vocabulary', desc: 'Science vocabulary in Hindi handled correctly.' },
    ],
    related: ['hindi-worksheet-generator', 'ai-lesson-plan-generator', 'multilingual-ai-tool-for-teachers'],
  },
  'hindi-worksheet-generator': {
    h1: 'Hindi Worksheet Generator — हिंदी कार्यपत्रक',
    accent: '',
    eyebrow: 'हिंदी · Hindi worksheets',
    description: 'AI-powered Hindi worksheet generator with native Devanagari, answer keys, and DOCX/PDF download. Built for CBSE Hindi & State Board teachers.',
    keywords: ['Hindi worksheet generator', 'हिंदी कार्यपत्रक', 'CBSE Hindi worksheet'],
    benefits: [
      { icon: Languages, title: 'Native Devanagari output', desc: 'Sentences read naturally — not Roman→Hindi conversions.' },
      { icon: Check,     title: 'Answer keys in Hindi',     desc: 'Marking schemes generated alongside.' },
      { icon: Check,     title: 'CBSE & State Board ready', desc: 'Patterns match your board\'s worksheet conventions.' },
    ],
    related: ['hindi-lesson-plan-generator', 'worksheet-generator', 'multilingual-ai-tool-for-teachers'],
  },
  'marathi-worksheet-generator': {
    h1: 'Marathi Worksheet Generator — मराठी कार्यपत्रिका',
    accent: '',
    eyebrow: 'मराठी · Marathi',
    description: 'Marathi worksheet generator with native Devanagari, Maharashtra State Board patterns, and DOCX/PDF download. The fastest path to a printable Marathi worksheet.',
    keywords: ['Marathi worksheet generator', 'मराठी कार्यपत्रिका', 'Maharashtra Board worksheet'],
    benefits: [
      { icon: Languages, title: 'Native Marathi',     desc: 'Generates in proper Marathi register, not transliterated.' },
      { icon: Check,     title: 'Maha Board ready',   desc: 'Pattern-aware for Maharashtra State Board.' },
      { icon: Check,     title: 'Grade-band tone',    desc: 'Right vocabulary for primary to senior secondary.' },
    ],
    related: ['hindi-worksheet-generator', 'worksheet-generator', 'multilingual-ai-tool-for-teachers'],
  },
  'bengali-quiz-generator': {
    h1: 'Bengali Quiz Generator — বাংলা কুইজ',
    accent: '',
    eyebrow: 'বাংলা · Bengali',
    description: 'Generate Bengali quizzes — MCQs, true/false, fill-in-the-blanks, short answer — in native script with answer keys. Built for West Bengal Board, ICSE and CBSE-Bengali teachers.',
    keywords: ['Bengali quiz generator', 'বাংলা কুইজ', 'WBBSE quiz', 'Bengali MCQ maker'],
    benefits: [
      { icon: Languages, title: 'Native Bengali script',   desc: 'Proper Bangla — not transliterated approximations.' },
      { icon: Check,     title: 'WBBSE / ICSE patterns',   desc: 'Quiz patterns aware of your board\'s conventions.' },
      { icon: Check,     title: 'Bloom-aligned',           desc: 'Cognitive-level controlled — Remember to Analyse.' },
    ],
    related: ['quiz-generator', 'tamil-question-paper-generator', 'multilingual-ai-tool-for-teachers'],
  },
  'tamil-question-paper-generator': {
    h1: 'Tamil Question Paper Generator — தமிழ் வினாத்தாள்',
    accent: '',
    eyebrow: 'தமிழ் · Tamil',
    description: 'Generate Tamil question papers — board-pattern aware, blueprint matchable, with validated section-marks arithmetic. For TN State Board & CBSE-Tamil teachers.',
    keywords: ['Tamil question paper generator', 'தமிழ் வினாத்தாள்', 'Tamil Nadu Board question paper'],
    benefits: [
      { icon: Languages, title: 'Native Tamil script', desc: 'Proper Tamil with appropriate grade-band vocabulary.' },
      { icon: Check,     title: 'TN State Board ready', desc: 'Patterns aware of Tamil Nadu Board conventions.' },
      { icon: Check,     title: 'Validated arithmetic', desc: 'Section totals always match item-level marks.' },
    ],
    related: ['question-paper-generator', 'bengali-quiz-generator', 'multilingual-ai-tool-for-teachers'],
  },
  'punjabi-assignment-generator': {
    h1: 'Punjabi Assignment Generator — ਪੰਜਾਬੀ ਨਿਯੁਕਤੀ',
    accent: '',
    eyebrow: 'ਪੰਜਾਬੀ · Punjabi',
    description: 'Punjabi assignment generator with native Gurmukhi, varied formats (project, research, practice), and model-answer scaffolding. For PSEB & CBSE-Punjab teachers.',
    keywords: ['Punjabi assignment generator', 'ਪੰਜਾਬੀ ਅਸਾਈਨਮੈਂਟ', 'Punjab Board assignment'],
    benefits: [
      { icon: Languages, title: 'Native Gurmukhi',         desc: 'Proper Punjabi register — not Romanised approximations.' },
      { icon: Check,     title: 'PSEB pattern aware',      desc: 'Aligned to Punjab School Education Board conventions.' },
      { icon: Check,     title: 'Multiple formats',        desc: 'Project · Research · Practice · Application.' },
    ],
    related: ['hindi-lesson-plan-generator', 'multilingual-ai-tool-for-teachers', 'ai-tool-for-teachers'],
  },
  'urdu-teaching-material-generator': {
    h1: 'Urdu Teaching Material Generator — اردو تدریسی مواد',
    accent: '',
    eyebrow: 'اُردُو · Urdu',
    description: 'AI Urdu teaching material generator — lesson plans, worksheets, summaries — in native Nastaliq script with right-to-left typography. Built for Urdu-medium schools across India.',
    keywords: ['Urdu teaching material generator', 'اردو تدریسی مواد', 'Urdu lesson plan'],
    benefits: [
      { icon: Languages, title: 'Native Nastaliq · RTL',     desc: 'Proper right-to-left typography in DOCX and PDF.' },
      { icon: Check,     title: 'Urdu-medium ready',         desc: 'Built for Urdu-medium schools nationwide.' },
      { icon: Check,     title: 'Subject-aware register',    desc: 'Science / literature / social-studies handled correctly.' },
    ],
    related: ['hindi-lesson-plan-generator', 'multilingual-ai-tool-for-teachers', 'ai-tool-for-teachers'],
  },
};

// ─── Shared FAQ across SEO landing pages ────────────────
const COMMON_FAQ = [
  { q: 'Which boards are supported?', a: 'CBSE, ICSE / ISC, IGCSE (Cambridge), IB (MYP / DP), CIS, and all major State Boards across grades 1–12.' },
  { q: 'Is the platform safe for student and teacher data?', a: 'Yes. Servers are in India, data is encrypted at rest (AES-256) and in transit (TLS 1.2+), and student data is never used to train AI models. School plans include a signed Data Processing Addendum.' },
  { q: 'How do I get started?', a: 'Click "Get Started Free" — signup takes under 60 seconds and the free tier is available forever. No credit card required.' },
  { q: 'Can a school chain or district use ActanonEdu?', a: 'Yes — Enterprise plans are designed for chains and districts with multi-school consoles, SSO, and API access. Talk to the founder via the contact page.' },
];

// ─── The actual page component ──────────────────────────
export function SEOLandingPage({ slug }) {
  const cfg = LANDING_PAGES[slug];
  if (!cfg) return null;

  const path = `/${slug}`;
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: cfg.eyebrow, url: path },
  ];
  const allFaq = [...COMMON_FAQ];

  return (
    <MarketingLayout>
      <SEO
        title={cfg.h1}
        description={cfg.description}
        path={path}
        keywords={cfg.keywords}
        breadcrumbs={breadcrumbs}
        faq={allFaq}
      />

      <PageHero
        eyebrow={cfg.eyebrow}
        title={cfg.h1}
        accent={cfg.accent}
        subtitle={cfg.description}
        testid={`seo-${slug}-hero`}
      >
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/signup">
            <Button size="lg" className="bg-mk-blue hover:bg-mk-deep text-white h-12 px-7 font-semibold w-full sm:w-auto" data-testid={`seo-${slug}-cta-primary`}>
              Get Started Free <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/school-pilot-program">
            <Button size="lg" variant="outline" className="h-12 px-7 font-semibold w-full sm:w-auto border-2" data-testid={`seo-${slug}-cta-pilot`}>
              Request School Pilot
            </Button>
          </Link>
        </div>
        <div className="mt-6"><TrustBadges /></div>
      </PageHero>

      <Section tone="muted" id="why-actanonedu">
        <SectionHeader eyebrow="Why ActanonEdu" title="Why teachers choose ActanonEdu for this." />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid={`seo-${slug}-why`}>
          {cfg.benefits.map((b, i) => (
            <FeatureCard key={i} icon={b.icon} name={b.title} summary={b.desc} tone={['indigo','emerald','amber','rose','violet','cyan'][i % 6]} />
          ))}
        </div>
      </Section>

      <Section id="how-it-works">
        <SectionHeader eyebrow="How it works" title="Three steps from sign-up to download." />
        <div className="grid md:grid-cols-3 gap-5" data-testid={`seo-${slug}-how`}>
          {[
            { step: '1', title: 'Sign up free', desc: 'Email + password. No credit card. Under 60 seconds.' },
            { step: '2', title: 'Pick subject & board', desc: 'Drop-down menus for class, subject, chapter, and language.' },
            { step: '3', title: 'Generate & download', desc: 'DOCX, PDF, or PPTX in 10–30 seconds. Edit if you want.' },
          ].map((s) => (
            <div key={s.step} className="relative rounded-2xl border border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy p-6">
              <div className="absolute -top-3 -left-3 h-10 w-10 rounded-xl bg-gradient-to-br from-mk-blue to-mk-blue text-white grid place-items-center font-bold shadow-md">{s.step}</div>
              <h3 className="font-bold pl-4 text-lg">{s.title}</h3>
              <p className="text-sm text-mk-muted dark:text-slate-300 mt-1 pl-4 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Internal linking — related landing pages */}
      {cfg.related?.length > 0 && (
        <Section tone="muted">
          <SectionHeader eyebrow="Explore related" title="Other tools in this cluster." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {cfg.related.map((r) => {
              const rel = LANDING_PAGES[r];
              if (!rel) return null;
              return (
                <Link key={r} to={`/${r}`} className="block rounded-xl border border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy p-4 hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid={`seo-${slug}-related-${r}`}>
                  <div className="text-xs uppercase tracking-wider text-mk-blue dark:text-mk-cyan mb-1">{rel.eyebrow}</div>
                  <div className="font-semibold text-sm leading-snug">{rel.h1}</div>
                </Link>
              );
            })}
          </div>
        </Section>
      )}

      <Section>
        <SectionHeader eyebrow="FAQ" title="Frequently asked questions." />
        <FAQAccordion items={allFaq} testid={`seo-${slug}-faq`} />
      </Section>

      <CTAStrip
        title="Get started in under a minute."
        subtitle="Free tier forever. No credit card. All Indian boards. 7 languages."
        primary={{ label: 'Get Started Free', href: '/signup' }}
        secondary={{ label: 'Talk to Founder', wa: `Hi Founder! I came from the ${cfg.h1} page.` }}
        testid={`seo-${slug}-final-cta`}
      />
    </MarketingLayout>
  );
}

// ─── React-Router-friendly thin wrappers (one per route) ───
export function makeSEOLandingComponent(slug) {
  // eslint-disable-next-line react/display-name
  const Comp = () => <SEOLandingPage slug={slug} />;
  Comp.displayName = `SEOLanding(${slug})`;
  return Comp;
}
